import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { WhatsAppCampaignAPI, type WhatsAppMediaType } from "src/services/WhatsAppCampaignAPI";
import { ApiError } from "src/services/HttpUtils";
import { BYTES_PER_MB, MEDIA_RULES, WHATSAPP_EDITOR_KEY } from "../WhatsAppCampaignConstants";
import type { WhatsAppMediaAttachment, WhatsAppMediaState } from "../WhatsAppCampaignTypes";
import { readImageDimensions } from "../WhatsAppCampaignUtils";

const ERRORS_KEY = `${WHATSAPP_EDITOR_KEY}.errors`;

export const useWhatsAppMediaUpload = (mediaType: WhatsAppMediaType | null) => {
  const { t } = useTranslation();
  const [attachment, setAttachment] = useState<WhatsAppMediaAttachment | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mediaUrlRef = useRef<string | null>(null);
  const requestRef = useRef(0);

  const revokeUrl = useCallback(() => {
    if (mediaUrlRef.current) {
      URL.revokeObjectURL(mediaUrlRef.current);
      mediaUrlRef.current = null;
    }
  }, []);

  const discard = useCallback(() => {
    requestRef.current += 1;
    revokeUrl();
    setAttachment(null);
    setError(null);
    setIsUploading(false);
  }, [revokeUrl]);

  useEffect(
    () => () => {
      requestRef.current += 1;
      revokeUrl();
    },
    [revokeUrl]
  );

  const attach = useCallback(
    async (file: File) => {
      if (!mediaType) {
        return;
      }
      const { accept, maxSizeMb } = MEDIA_RULES[mediaType];
      if (!accept.includes(file.type)) {
        setError(t(`${ERRORS_KEY}.mediaType.${mediaType}`));
        return;
      }
      if (file.size > maxSizeMb * BYTES_PER_MB) {
        setError(t(`${ERRORS_KEY}.mediaSize`, { size: maxSizeMb }));
        return;
      }

      const requestId = ++requestRef.current;
      const previewUrl = mediaType === "image" ? URL.createObjectURL(file) : null;
      setError(null);
      setIsUploading(true);
      try {
        const dimensions = previewUrl ? await readImageDimensions(previewUrl) : null;
        const response = await WhatsAppCampaignAPI.uploadMedia(mediaType, file);
        if (!response.success || !response.media) {
          throw new ApiError(response.message ?? "");
        }
        if (requestId !== requestRef.current) {
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
          }
          return;
        }
        revokeUrl();
        mediaUrlRef.current = previewUrl;
        setAttachment({
          uploaded: response.media,
          fileName: response.media.file_name || file.name,
          sizeBytes: response.media.size_bytes ?? file.size,
          previewUrl,
          width: dimensions?.width ?? null,
          height: dimensions?.height ?? null,
        });
      } catch (uploadError) {
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
        if (requestId === requestRef.current) {
          setError(
            uploadError instanceof ApiError && uploadError.message
              ? uploadError.message
              : t(`${ERRORS_KEY}.mediaUpload`)
          );
        }
      } finally {
        if (requestId === requestRef.current) {
          setIsUploading(false);
        }
      }
    },
    [mediaType, revokeUrl, t]
  );

  const media: WhatsAppMediaState = useMemo(
    () => ({ attachment, isUploading, error }),
    [attachment, isUploading, error]
  );

  return { media, attach, discard, setError };
};
